import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Plus, Trash2, LogOut, Eye, EyeOff, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminGallery({ user, setUser }) {
  const [pairs, setPairs] = useState([]);
  const [soloImages, setSoloImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPairForm, setShowPairForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchGallery();
  }, [user, navigate]);

  const fetchGallery = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/gallery`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const allImages = data.images || [];

      // Group by pair_id
      const pairMap = {};
      const solo = [];
      allImages.forEach(img => {
        if (img.pair_id) {
          if (!pairMap[img.pair_id]) pairMap[img.pair_id] = { pair_id: img.pair_id, title: '', before: null, after: null, is_active: true };
          if (img.pair_role === 'before') pairMap[img.pair_id].before = img;
          if (img.pair_role === 'after') pairMap[img.pair_id].after = img;
          if (img.title) pairMap[img.pair_id].title = img.title;
          pairMap[img.pair_id].is_active = img.is_active;
        } else {
          solo.push(img);
        }
      });

      setPairs(Object.values(pairMap).filter(p => p.before && p.after));
      setSoloImages(solo);
    } catch (error) {
      toast.error('Error al cargar galería');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePair = async (pairId) => {
    if (!confirm('¿Eliminar este par de imágenes?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/admin/gallery/pair/${pairId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Par eliminado');
      fetchGallery();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleTogglePair = async (pairId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/admin/gallery/pair/${pairId}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Visibilidad actualizada');
      fetchGallery();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleSwapPair = async (pairId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/gallery/pair/${pairId}/swap`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('swap');
      toast.success('Antes/Después intercambiados');
      fetchGallery();
    } catch (error) {
      toast.error('Error al intercambiar');
    }
  };

  const handleDeleteSolo = async (imageId) => {
    if (!confirm('¿Eliminar esta imagen?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/admin/gallery/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Imagen eliminada');
      fetchGallery();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b overflow-x-auto">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6 whitespace-nowrap">
            <Link to="/admin" className="text-gray-600 hover:text-black">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black">Newsletter</Link>
            <Link to="/admin/gallery" className="font-medium text-black border-b-2 border-black pb-2">Fotos Portada</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }} data-testid="gallery-title">Fotos Portada</h2>
          <Button onClick={() => setShowPairForm(true)} className="bg-black text-white" data-testid="add-pair-btn">
            <Plus className="w-4 h-4 mr-2" /> Añadir Par (Antes/Después)
          </Button>
        </div>

        <div className="bg-blue-50 p-4 rounded border border-blue-200 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Sube pares de fotos Antes/Después.</strong> Se mostrarán en la portada de la tienda, rotando aleatoriamente. Cada par muestra las dos imágenes lado a lado.
          </p>
        </div>

        {showPairForm && (
          <PairUploadForm 
            onClose={() => setShowPairForm(false)}
            onSuccess={() => { setShowPairForm(false); fetchGallery(); }}
          />
        )}

        {/* Pairs */}
        {pairs.length > 0 && (
          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold">Pares Antes/Después ({pairs.length})</h3>
            {pairs.map((pair) => (
              <div key={pair.pair_id} className="bg-white border-2 border-black p-4" data-testid={`pair-${pair.pair_id}`}>
                {pair.title && <h4 className="font-bold text-lg mb-3">{pair.title}</h4>}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="relative">
                    <img src={pair.before?.url} alt="Antes" className="w-full h-48 object-cover" />
                    <span className="absolute top-2 left-2 bg-black text-white text-xs px-3 py-1 font-bold">ANTES</span>
                  </div>
                  <div className="relative">
                    <img src={pair.after?.url} alt="Después" className="w-full h-48 object-cover" />
                    <span className="absolute top-2 right-2 bg-black text-white text-xs px-3 py-1 font-bold">DESPUÉS</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleTogglePair(pair.pair_id)}
                    className={`flex items-center gap-2 px-3 py-1 rounded text-sm ${
                      pair.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}
                    data-testid={`toggle-pair-${pair.pair_id}`}
                  >
                    {pair.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    {pair.is_active ? 'Visible' : 'Oculta'}
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSwapPair(pair.pair_id)}
                      className="flex items-center gap-1 px-3 py-1 rounded text-sm bg-blue-100 text-blue-800 hover:bg-blue-200"
                      data-testid={`swap-pair-${pair.pair_id}`}
                      title="Intercambia el orden: la imagen izquierda pasa a la derecha y viceversa"
                    >
                      <ArrowLeftRight className="w-4 h-4" />
                      Intercambiar antes/después
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePair(pair.pair_id)}
                      className="text-red-600"
                      data-testid={`delete-pair-${pair.pair_id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Eliminar Par
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Legacy solo images */}
        {soloImages.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4">Imágenes Individuales ({soloImages.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {soloImages.map((image) => (
                <div key={image.id} className="bg-white border border-gray-300 p-4">
                  <img src={image.url} alt={image.title} className="w-full h-48 object-cover mb-3" />
                  <h3 className="font-bold mb-1">{image.title || 'Sin título'}</h3>
                  <div className="flex items-center justify-end">
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteSolo(image.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pairs.length === 0 && soloImages.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white border-2 border-black" data-testid="empty-gallery">
            <ImageIcon className="w-16 h-16 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No hay fotos de portada</p>
            <p className="text-sm mt-2">Añade pares de fotos antes/después de tus clientes</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PairUploadForm({ onClose, onSuccess }) {
  const [title, setTitle] = useState('');
  const [beforeUrl, setBeforeUrl] = useState('');
  const [afterUrl, setAfterUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!beforeUrl || !afterUrl) {
      toast.error('Introduce ambas URLs (antes y después)');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/gallery/pair`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title.trim(),
          before_url: beforeUrl.trim(),
          after_url: afterUrl.trim()
        })
      });

      if (response.ok) {
        toast.success('Par de fotos añadido');
        onSuccess();
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Error al crear par');
      }
    } catch (error) {
      console.error('Gallery pair error:', error);
      toast.error('Error de conexión: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white p-6 border-2 border-black mb-6" data-testid="pair-upload-form">
      <h3 className="text-xl font-bold mb-4">Nuevo Par Antes/Después</h3>
      
      <div className="mb-4">
        <Label>Título del par (opcional)</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ej: Cliente - Prótesis Modelo X"
          data-testid="pair-title-input"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border-2 border-dashed border-gray-300 p-4 rounded">
          <h4 className="font-bold mb-3 text-center">ANTES</h4>
          <Label>URL de la imagen</Label>
          <Input
            value={beforeUrl}
            onChange={(e) => setBeforeUrl(e.target.value)}
            placeholder="https://ejemplo.com/foto-antes.jpg"
            data-testid="before-url-input"
          />
          {beforeUrl && (
            <div className="mt-3 relative">
              <img src={beforeUrl} alt="Antes" className="w-full h-40 object-cover border" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 font-bold">ANTES</span>
            </div>
          )}
        </div>

        <div className="border-2 border-dashed border-gray-300 p-4 rounded">
          <h4 className="font-bold mb-3 text-center">DESPUÉS</h4>
          <Label>URL de la imagen</Label>
          <Input
            value={afterUrl}
            onChange={(e) => setAfterUrl(e.target.value)}
            placeholder="https://ejemplo.com/foto-despues.jpg"
            data-testid="after-url-input"
          />
          {afterUrl && (
            <div className="mt-3 relative">
              <img src={afterUrl} alt="Después" className="w-full h-40 object-cover border" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="absolute top-2 right-2 bg-black text-white text-xs px-2 py-1 font-bold">DESPUÉS</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <Button onClick={handleSubmit} className="bg-black text-white" disabled={uploading || !beforeUrl || !afterUrl} data-testid="save-pair-btn">
          {uploading ? 'Guardando...' : 'Guardar Par'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
      </div>
    </div>
  );
}
